import { MutableRefObject, useCallback, useRef, useState } from 'react'
import {
  BulkSheet,
  BulkSheetSpecificProps,
} from '../../../containers/BulkSheet'
import { UserDetail } from '../../../../lib/functions/user'
import { UserRow, UserState } from '../UserOptions'
import { RoleChangeItem } from '../components/RoleChangeConfirmationDialog'

type UseRoleChangeConfirmationReturn = {
  dialogOpen: boolean
  roleChanges: RoleChangeItem[]
  selectedUserUuids: Set<string>
  handleToggleUser: (userUuid: string) => void
  handleToggleAll: () => void
  handleConfirm: () => void
  handleCancel: () => void
  handleSubmit: () => void
  projectRoleUpdateUserUuidsRef: MutableRefObject<string[]>
  beforeSubmitRef: MutableRefObject<(() => boolean) | undefined>
}

export const useRoleChangeConfirmation = (
  bulkSheet:
    | BulkSheet<BulkSheetSpecificProps, UserDetail, UserRow, UserState>
    | undefined
): UseRoleChangeConfirmationReturn => {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [roleChanges, setRoleChanges] = useState<RoleChangeItem[]>([])
  const [selectedUserUuids, setSelectedUserUuids] = useState<Set<string>>(
    new Set()
  )
  const projectRoleUpdateUserUuidsRef = useRef<string[]>([])
  const skipBeforeSubmitRef = useRef(false)

  const checkRoleChanges = useCallback((): boolean => {
    if (!bulkSheet) return true

    const data = bulkSheet.rowDataManager.getDataForBatchUpdate()
    const changes = data.edited.filter(
      ({ before, after }) => before.role?.uuid !== after.role?.uuid
    )

    if (changes.length === 0) return true

    const items: RoleChangeItem[] = changes.map(({ before, after }) => ({
      userUuid: after.uuid,
      userName: after.name || '',
      oldRoleName: before.role?.name || '',
      newRoleName: after.role?.name || '',
    }))

    setRoleChanges(items)
    setSelectedUserUuids(new Set())
    setDialogOpen(true)
    return false
  }, [bulkSheet])

  const beforeSubmitRef = useRef<(() => boolean) | undefined>(undefined)
  beforeSubmitRef.current = () => {
    if (skipBeforeSubmitRef.current) {
      skipBeforeSubmitRef.current = false
      return true
    }
    return checkRoleChanges()
  }

  const handleSubmit = useCallback(() => {
    bulkSheet?.onSubmit()
  }, [bulkSheet])

  const handleToggleUser = useCallback((userUuid: string) => {
    setSelectedUserUuids(prev => {
      const next = new Set(prev)
      if (next.has(userUuid)) {
        next.delete(userUuid)
      } else {
        next.add(userUuid)
      }
      return next
    })
  }, [])

  const handleToggleAll = useCallback(() => {
    setSelectedUserUuids(prev => {
      if (prev.size === roleChanges.length) {
        return new Set()
      }
      return new Set(roleChanges.map(item => item.userUuid))
    })
  }, [roleChanges])

  const handleConfirm = useCallback(() => {
    projectRoleUpdateUserUuidsRef.current = Array.from(selectedUserUuids)
    setDialogOpen(false)
    skipBeforeSubmitRef.current = true
    bulkSheet?.onSubmit()
  }, [bulkSheet, selectedUserUuids])

  const handleCancel = useCallback(() => {
    setDialogOpen(false)
    setRoleChanges([])
    setSelectedUserUuids(new Set())
  }, [])

  return {
    dialogOpen,
    roleChanges,
    selectedUserUuids,
    handleToggleUser,
    handleToggleAll,
    handleConfirm,
    handleCancel,
    handleSubmit,
    projectRoleUpdateUserUuidsRef,
    beforeSubmitRef,
  }
}
